import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Button,
  Chip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import { FilterList, TrendingUp, PictureAsPdf, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import ExportButton from '../../../components/ExportButton/ExportButton';

const ProjectReport = () => {
  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    stage: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const stages = useMemo(() => ([
    'Lead Generation',
    'Site Survey',
    'Quotation',
    'Order Confirmed',
    'Installation Planning',
    'Installation',
    'Quality Check',
    'Commissioning',
    'Handover',
    'AMC / Maintenance',
  ]), []);

  const rows = useMemo(() => ([
    { ref: 'PRJ-10201', name: 'Rooftop A1', customer: 'Priya', stage: 'Quality Check', status: 'In Progress', start: '2025-10-26', end: '-', amount: 145000 },
    { ref: 'PRJ-10202', name: 'Ground Mount C2', customer: 'Rahul', stage: 'Installation', status: 'Pending', start: '2025-10-28', end: '-', amount: 320000 },
    { ref: 'PRJ-10190', name: 'Battery Storage B1', customer: 'Amit', stage: 'Commissioning', status: 'Completed', start: '2025-10-20', end: '2025-10-27', amount: 210000 },
  ]), []);

  const orders = useMemo(() => ([
    { orderNo: 'ORD-5011', ref: 'PRJ-10201', customer: 'Priya', items: 12, date: '2025-10-26', status: 'Processing', total: 145000 },
    { orderNo: 'ORD-5012', ref: 'PRJ-10202', customer: 'Rahul', items: 18, date: '2025-10-28', status: 'Pending', total: 320000 },
    { orderNo: 'ORD-4987', ref: 'PRJ-10190', customer: 'Amit', items: 9, date: '2025-10-20', status: 'Completed', total: 210000 },
  ]), []);

  const statusColor = (s) => {
    if (s === 'Completed') return 'success';
    if (s === 'In Progress' || s === 'Processing') return 'warning';
    return 'default';
  };

  const filteredProjects = rows.filter(r =>
    (!filters.search || `${r.ref} ${r.name} ${r.customer}`.toLowerCase().includes(filters.search.toLowerCase())) &&
    (!filters.stage || r.stage === filters.stage) &&
    (!filters.status || r.status === filters.status)
  );

  const filteredOrders = orders.filter(r =>
    (!filters.search || `${r.orderNo} ${r.ref} ${r.customer}`.toLowerCase().includes(filters.search.toLowerCase())) &&
    (!filters.status || r.status === filters.status)
  );

  const totalProjects = filteredProjects.length;
  const completedProjects = filteredProjects.filter(r => r.status === 'Completed').length;
  const totalValue = filteredProjects.reduce((sum, r) => sum + r.amount, 0);


  const downloadBlob = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    if (tab === 0) {
      const headerRow = ['Ref', 'Project', 'Customer', 'Stage', 'Status', 'Start', 'End', 'Amount'];
      const rowsCsv = filteredProjects.map(r => [r.ref, r.name, r.customer, r.stage, r.status, r.start, r.end, r.amount]);
      const csv = [headerRow, ...rowsCsv].map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
      downloadBlob(csv, 'project-report.csv', 'text/csv;charset=utf-8;');
    } else {
      const headerRow = ['Order #', 'Project Ref', 'Customer', 'Items', 'Date', 'Status', 'Total'];
      const rowsCsv = filteredOrders.map(r => [r.orderNo, r.ref, r.customer, r.items, r.date, r.status, r.total]);
      const csv = [headerRow, ...rowsCsv].map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
      downloadBlob(csv, 'order-list.csv', 'text/csv;charset=utf-8;');
    }
  };

  const exportPdf = () => {
    const title = tab === 0 ? 'Project Report' : 'Order List';
    const win = window.open('', '_blank', 'width=1024,height=768');
    if (!win) return;
    const styles = `
      <style>
        body{font-family: Arial, sans-serif; padding:16px}
        h2{margin:0 0 12px 0}
        table{width:100%; border-collapse:collapse}
        th,td{border:1px solid #ccc; padding:6px 8px; font-size:12px; text-align:left}
        th{background:#f0f0f0}
      </style>
    `;
    const thead = tab === 0
      ? '<tr><th>Ref</th><th>Project</th><th>Customer</th><th>Stage</th><th>Status</th><th>Start</th><th>End</th><th>Amount</th></tr>'
      : '<tr><th>Order #</th><th>Project Ref</th><th>Customer</th><th>Items</th><th>Date</th><th>Status</th><th>Total</th></tr>';
    const tbody = tab === 0
      ? filteredProjects.map(r => `<tr><td>${r.ref}</td><td>${r.name}</td><td>${r.customer}</td><td>${r.stage}</td><td>${r.status}</td><td>${r.start}</td><td>${r.end}</td><td>${r.amount}</td></tr>`).join('')
      : filteredOrders.map(r => `<tr><td>${r.orderNo}</td><td>${r.ref}</td><td>${r.customer}</td><td>${r.items}</td><td>${r.date}</td><td>${r.status}</td><td>${r.total}</td></tr>`).join('');
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"/>${styles}</head><body><h2>${title}</h2><table><thead>${thead}</thead><tbody>${tbody}</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    // simulate API delay
    setTimeout(() => {
      setSearchLoading(false);
    }, 600);
  };

  const handleClear = () => {
    setFilters({ search: '', stage: '', status: '', dateFrom: '', dateTo: '' });
    setHasSearched(false);
    setSearchLoading(false);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Order / Project Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze project progress and order details
            </Typography>
          </Box>
          <ExportButton
            onExportPdf={exportPdf}
            onExportCsv={exportCsv}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Projects</Typography>
              </Box>
              <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                <Chip label={`Total: ${totalProjects}`} />
                <Chip label={`Completed: ${completedProjects}`} color="success" />
                <Chip label={`Value: ₹${totalValue.toLocaleString('en-IN')}`} color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Search (Ref / Name / Customer)" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stage</InputLabel>
                <Select value={filters.stage} label="Stage" onChange={(e) => setFilters(prev => ({ ...prev, stage: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  {stages.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Processing">Processing</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.dateFrom} onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.dateTo} onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} onClick={handleSearch} disabled={searchLoading} sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>{searchLoading ? 'Searching...' : 'Search'}</Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" startIcon={<ClearIcon />} onClick={handleClear}>Clear</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Project Report" />
            <Tab label="Order List" />
          </Tabs>

          {tab === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#536DFE' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Ref</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Stage</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Start</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>End</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.map(r => (
                    <TableRow key={r.ref} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{r.ref}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>{r.customer}</TableCell>
                      <TableCell><Chip label={r.stage} size="small" color="primary" variant="outlined" /></TableCell>
                      <TableCell><Chip label={r.status} size="small" color={statusColor(r.status)} /></TableCell>
                      <TableCell>{r.start}</TableCell>
                      <TableCell>{r.end}</TableCell>
                      <TableCell>₹{r.amount.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#536DFE' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Order #</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project Ref</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Items</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.map(o => (
                    <TableRow key={o.orderNo} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{o.orderNo}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{o.ref}</TableCell>
                      <TableCell>{o.customer}</TableCell>
                      <TableCell>{o.items}</TableCell>
                      <TableCell>{o.date}</TableCell>
                      <TableCell><Chip label={o.status} size="small" color={statusColor(o.status)} /></TableCell>
                      <TableCell>₹{o.total.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectReport;


